import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  ChevronDown, ChevronRight, CheckCircle2, TrendingUp, TrendingDown, AlertTriangle, Pencil,
} from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { Switch } from '../../../../foundation/altzero_base/base/components/ui/switch';
import { chargeMappingService, type RetailerRow, type ResolveResult } from '../../services/chargeMappingService';
import { chargeMapService } from '../../services/chargeMapService';
import { showsEnvironmental } from '../../utils/environmental';
import { effectiveExclusions } from '../../utils/calcExclusions';
import { RATE_ROWS, canonicalChargeName } from '../../utils/chargeNames';
import type { ChargeMapRow } from '../../utils/chargeMap';
import type { NameAliases } from '../../utils/nameAliases';
import { notCompared } from '../../lib/notCompared';
import { buildMapContext, comparisonRowsBySection } from '../../lib/comparisonSections';
import {
  band, canonicalSectionFor,
  comparisonTotals, excludedFromVariance, formatQty, formatUsage, formatRate, invoiceLineCount,
  isRowExcluded, norm,
  qtyUnitsDisagree, rateUnitsDisagree, usageUnitsDisagree,
  type Band, type ChargeRow,
} from '../../utils/comparisonRows';
import type {
  BvBill, ShadowBillSection, ActualBillResult, ShadowBillResult, CalcExclusion,
} from '../../types/billTypes';
import { editedSummary } from '../../lib/effectiveInvoice';
import { InvoiceCompareDialog } from './InvoiceCompareDialog';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

const THRESHOLD = 0.50;
type VarianceKind = 'matched' | 'over' | 'under';

function classify(shadow: number, actual: number | null): VarianceKind | null {
  if (actual == null) return null;
  const diff = actual - shadow;
  if (Math.abs(diff) <= THRESHOLD) return 'matched';
  return diff > 0 ? 'over' : 'under';
}

function pctStr(shadow: number, actual: number): string {
  if (shadow === 0) return '—';
  const p = ((actual - shadow) / Math.abs(shadow)) * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`;
}

// ─── Variance Cell ────────────────────────────────────────────────────────────

function VarianceCell({
  shadow, actual, bold = false,
}: { shadow: number; actual: number | null; bold?: boolean }) {
  if (actual == null) {
    return <td className="px-4 py-3 text-center"><span className="text-gray-300 text-xs">—</span></td>;
  }
  const diff = actual - shadow;
  const kind = classify(shadow, actual)!;

  if (kind === 'matched') {
    return (
      <td className="px-4 py-3 text-center">
        <span className={cn(
          'inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-medium text-xs bg-emerald-50 text-emerald-700 border border-emerald-200',
          bold && 'text-sm',
        )}>
          <CheckCircle2 className="h-3 w-3" /> Matched
        </span>
      </td>
    );
  }

  return (
    <td className="px-4 py-3 text-center">
      <div className="flex flex-col items-center gap-0.5">
        <span className={cn(
          'inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-medium text-xs border',
          kind === 'over'
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-amber-50 text-amber-700 border-amber-200',
          bold && 'text-sm',
        )}>
          {kind === 'over'
            ? <TrendingUp className="h-3 w-3" />
            : <TrendingDown className="h-3 w-3" />}
          {diff >= 0 ? '+' : ''}{fmt(diff)}
        </span>
        <span className={cn(
          'text-[10px] font-semibold tabular-nums',
          kind === 'over' ? 'text-red-500' : 'text-amber-500',
        )}>
          {pctStr(shadow, actual)}
        </span>
      </div>
    </td>
  );
}

// ─── Section config ───────────────────────────────────────────────────────────

const SECTIONS: { key: ShadowBillSection; label: string }[] = [
  { key: 'retail',        label: 'Retail Energy Charges' },
  { key: 'distribution',  label: 'Network / Distribution Charges' },
  { key: 'metering',      label: 'Metering Charges' },
  { key: 'environmental', label: 'Environmental Charges' },
  { key: 'other',         label: 'Other' },
];

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  bill: BvBill;
  shadowResult: ShadowBillResult;
  actualResult: ActualBillResult | null;
  /** Job 53: charges switched out of the calculation. An excluded charge leaves
   *  both sides, so the variance ignores it. Omitted on read-only views, which
   *  fall back to what is stored on the bill and render no switches — the same
   *  arrangement ShadowBillDocument uses. */
  exclusions?: CalcExclusion[];
  onExclusionsChange?: (next: CalcExclusion[]) => void;
  /** Job 20: correct a quantity without leaving the comparison. `sourceIndex`
   *  is the line's position in the corresponding result's `lines[]`.
   *
   *  Optional throughout, on the same principle as `onExclusionsChange`: a
   *  read-only host (the report, the site invoice tab) passes nothing and the
   *  cells render as plain text. Nothing here writes on its own — the host owns
   *  persistence, because expected and retailer figures go to different places
   *  (shadow_result override vs actual_edited_result) with different audit
   *  trails. */
  onEditShadowQty?: (sourceIndex: number, volume: number | null) => Promise<void>;
  onEditActualQty?: (sourceIndex: number, volume: number | null) => Promise<void>;
  onEditShadowUsage?: (sourceIndex: number, usage: number | null) => Promise<void>;
  onEditActualUsage?: (sourceIndex: number, usage: number | null) => Promise<void>;
  /** Rename ONE invoice line, on this bill only.
   *
   *  The retailer-wide charge alias is keyed on the invoice WORDING, so saving one renames every
   *  line that reads the same and carries into that retailer's later invoices. Correct for a
   *  standing rename, wrong when only one line was meant — reported as "mapping 1 section of
   *  invoice maps anything with a similar name also". Without this prop the picker offers only
   *  the retailer-wide scope, exactly as before. */
  onEditActualDescription?: (sourceIndex: number, description: string) => Promise<void>;
  /** Openers for rows whose quantity sums several lines and so cannot take a
   *  single typed figure. Without these such rows simply stay read-only. */
  onOpenExpectedEditor?: () => void;
  onOpenRetailerEditor?: () => void;
};

type FilterMode = 'all' | 'issues';

// Tolerances for the rate/usage comparison. Invoices print rounded rates, so an
// exact match is not a realistic bar — these are "close enough that the
// difference is presentation, not pricing".
const RATE_TOLERANCE = 0.01;   // cents per unit
const VOLUME_TOLERANCE = 0.5;  // kWh / kVA / days

const num = (v: number | null, decimals: number): string =>
  v == null
    ? '—'
    : v.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/**
 * The QTY cell when it can be edited in place.
 *
 * Job 20 ("usage not adjustable, needs to be"): the quantity was already
 * editable in ExpectedInvoiceEditor and ParsedInvoiceEditor, but both live on
 * other tabs — and a validator finds a wrong quantity while reading the
 * comparison, not while reading either invoice on its own. Same reasoning that
 * put Job 53's exclusion switches on this table.
 *
 * Editing the quantity does NOT re-derive the amount, matching both editors and
 * the reasoning recorded in utils/shadowOverride.ts: rate units differ per line
 * (¢/kWh against $/day against $/meter/year), so a volume × rate helper would
 * produce confidently incorrect money. QTY is the record of how the amount was
 * reached; the amount is corrected on its own.
 */
function EditableQty({
  value, display, onSave,
}: {
  value: number | null;
  display: string;
  onSave: (next: number | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const begin = () => {
    setDraft(value == null ? '' : String(value));
    setEditing(true);
  };

  const commit = async () => {
    const trimmed = draft.trim();
    // Cleared means "no quantity recorded", not zero — same distinction the two
    // editors draw with numOrUndef. Zero is a real, billable quantity.
    const next = trimmed === '' ? null : Number(trimmed);
    if (next != null && !Number.isFinite(next)) { setEditing(false); return; }
    if (next === value) { setEditing(false); return; }

    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={begin}
        title="Click to correct this quantity"
        className={cn(
          'w-full text-right tabular-nums rounded px-1 -mx-1',
          'hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 transition-colors',
        )}
      >
        {display}
      </button>
    );
  }

  return (
    <input
      autoFocus
      type="number"
      step="any"
      disabled={saving}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void commit();
        // Escape abandons the edit — a validator who mistypes into the wrong row
        // needs a way out that does not write.
        if (e.key === 'Escape') setEditing(false);
      }}
      className="w-24 h-6 text-right tabular-nums rounded border border-blue-300 px-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  );
}

/**
 * One numeric cell — QTY or Usage — in whichever of three states applies:
 * editable in place, a hand-off to the full editor, or plain text.
 */
function QuantityCell({
  text, value, flagged, muted, lineCount, sourceIndex, onEdit, onOpenEditor, emptyHint,
}: {
  text: string;
  value: number | null;
  flagged: boolean;
  muted?: boolean;
  lineCount: number;
  sourceIndex: number | null;
  onEdit?: (sourceIndex: number, next: number | null) => Promise<void>;
  onOpenEditor?: () => void;
  /** Why this cell is legitimately empty, shown on hover so an em dash does not
   *  read as missing data. */
  emptyHint?: string;
}) {
  const className = cn(
    'px-3 py-1.5 text-right tabular-nums',
    flagged ? 'text-red-700 font-semibold' : muted ? 'text-gray-500' : 'text-gray-800',
  );

  if (onEdit && sourceIndex != null) {
    return (
      <td className={className}>
        <EditableQty value={value} display={text} onSave={(next) => onEdit(sourceIndex, next)} />
      </td>
    );
  }

  if (onOpenEditor && lineCount > 1) {
    return (
      <td className={className}>
        <button
          type="button"
          onClick={onOpenEditor}
          title={`This figure is the sum of ${lineCount} lines, so it cannot be corrected as one number. Opens the editor.`}
          className="inline-flex items-center gap-1 rounded px-1 -mx-1 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 transition-colors"
        >
          {text}
          <Pencil className="h-2.5 w-2.5 text-gray-400" />
        </button>
      </td>
    );
  }

  return (
    <td className={className} title={value == null ? emptyHint : undefined}>
      {text}
    </td>
  );
}

/** One of the two bands under a charge. `other` is the opposing side, used only
 *  to flag where the two disagree beyond tolerance. */
function BandRow({
  label, self, other, muted, onEditQty, onEditUsage, onOpenEditor,
}: {
  label: string;
  self: Band | null;
  other: Band | null;
  muted?: boolean;
  /** Absent when this side cannot be edited from here — the host passed no
   *  handler, so the cells render as plain text exactly as they always did. */
  onEditQty?: (sourceIndex: number, volume: number | null) => Promise<void>;
  onEditUsage?: (sourceIndex: number, usage: number | null) => Promise<void>;
  /** Opens the full editor for this side, for rows whose figures are a sum of
   *  several lines and so cannot take a single typed number. */
  onOpenEditor?: () => void;
}) {
  if (!self) {
    return (
      <tr className="bg-white">
        <td className="px-3 py-1.5 pl-8 text-[11px] text-gray-400 italic">{label}</td>
        <td colSpan={5} className="px-3 py-1.5 text-[11px] text-gray-400 italic">
          not present
        </td>
      </tr>
    );
  }

  const off = (a: number | null, b: number | null, tol: number) =>
    a != null && b != null && Math.abs(a - b) > tol;

  // Job 20: two rates quoted in different units are not comparable, so a
  // numeric difference between them is not a finding. Flag the mismatch instead.
  const unitsDiffer = rateUnitsDisagree(self, other);
  const rateOff = !unitsDiffer && off(self.rate, other?.rate ?? null, RATE_TOLERANCE);
  // Job 60: same reasoning one column across. Our metering QTY is a meter count
  // where a retailer may quote days, so the gap between them is not a variance.
  const qtyUnitsDiffer = qtyUnitsDisagree(self, other);
  const qtyOff = !qtyUnitsDiffer && off(self.qty, other?.qty ?? null, VOLUME_TOLERANCE);
  // Usage is compared on the same terms: a kWh figure against a days figure is
  // not a discrepancy, so the units have to agree before a gap means anything.
  const usageUnitsDiffer = usageUnitsDisagree(self, other);
  const usageOff = !usageUnitsDiffer && off(self.usage, other?.usage ?? null, VOLUME_TOLERANCE);
  const totalOff = off(self.total, other?.total ?? null, THRESHOLD);

  const cell = (content: string, flagged: boolean) => (
    <td className={cn(
      'px-3 py-1.5 text-right tabular-nums',
      flagged ? 'text-red-700 font-semibold' : muted ? 'text-gray-500' : 'text-gray-800',
    )}>
      {content}
    </td>
  );

  return (
    <tr className="bg-white">
      <td className={cn('px-3 py-1.5 pl-8 text-[11px]', muted ? 'text-gray-500' : 'text-gray-600')}>
        {label}
        {self.lineCount > 1 && (
          <span className="ml-1 text-[10px] text-gray-400">({self.lineCount} lines)</span>
        )}
      </td>
      <td className={cn(
        'px-3 py-1.5 text-right tabular-nums',
        rateOff ? 'text-red-700 font-semibold' : muted ? 'text-gray-500' : 'text-gray-800',
      )}>
        {/* A unit worked out from the line's own figures rather than recorded on
            it is marked, so "13.7540 ¢/kWh" on a bill calculated before units
            were stored does not read as something the calculation asserted. */}
        <span
          className={cn(self.rateUnitDerived && 'underline decoration-dotted decoration-gray-300 underline-offset-2')}
          title={self.rateUnitDerived
            ? 'Unit not recorded on this line — worked out from its rate, quantity and amount. Refresh to store it.'
            : undefined}
        >
          {formatRate(self)}
        </span>
        {unitsDiffer && (
          <span
            className="ml-1 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded align-middle"
            title="The two sides quote this rate in different units, so the figures are not directly comparable."
          >
            unit differs
          </span>
        )}
      </td>
      {/* QTY and Usage are separate columns and separately editable: metering is
          a meter count AND a number of days, and a validator may need to correct
          either one. Both are editable in place when exactly one line backs the
          row; an aggregated row offers the full editor instead. */}
      <QuantityCell
        text={formatQty(self)}
        value={self.qty}
        flagged={qtyOff}
        muted={muted}
        lineCount={self.lineCount}
        sourceIndex={self.sourceIndex}
        onEdit={onEditQty}
        onOpenEditor={onOpenEditor}
        emptyHint="This charge counts nothing — it is priced per unit consumed."
      />
      <QuantityCell
        text={formatUsage(self)}
        value={self.usage}
        flagged={usageOff}
        muted={muted}
        lineCount={self.lineCount}
        sourceIndex={self.sourceIndex}
        onEdit={onEditUsage}
        onOpenEditor={onOpenEditor}
      />
      {cell(self.lossFactor != null ? num(self.lossFactor, 4) : '—', false)}
      <td className={cn(
        'px-3 py-1.5 text-right tabular-nums font-medium',
        totalOff ? 'text-red-700' : self.total < 0 ? 'text-emerald-600' : 'text-gray-800',
      )}>
        {fmt(self.total)}
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BillComparisonTable({
  bill, shadowResult: sr, actualResult: apr, exclusions: exclusionsProp, onExclusionsChange,
  onEditShadowQty, onEditActualQty, onEditShadowUsage, onEditActualUsage,
  onEditActualDescription,
  onOpenExpectedEditor, onOpenRetailerEditor,
}: Props) {
  // A read-only host passes neither prop: exclusions still come off the bill so
  // the totals match what the Expected Invoice tab shows, but with no handler
  // there is nothing to switch and no switches are drawn.
  const exclusions = exclusionsProp
    ?? (Array.isArray(bill.calc_exclusions) ? bill.calc_exclusions : []);
  // What the totals actually run on: the stored toggles plus the site-level rule
  // that a site without GreenPower is not compared on environmental charges.
  // Only the stored list is ever written back — see effectiveExclusions.
  const effective = effectiveExclusions(bill, exclusions);
  const canToggle = typeof onExclusionsChange === 'function';
  const [comparingEdits, setComparingEdits] = useState(false);
  // Sections start expanded. The rate, quantity and loss-factor breakdown is the
  // reason this table exists, and hiding it behind a click read as "the rates are
  // missing" — collapse stays available for anyone who only wants section totals.
  const [expanded, setExpanded] = useState<Set<ShadowBillSection>>(
    () => new Set(SECTIONS.map((s) => s.key)),
  );
  const [filter, setFilter] = useState<FilterMode>('all');
  const [resolution, setResolution] = useState<(ResolveResult & { retailers: RetailerRow[] }) | null>(null);
  const [chargeMapRows, setChargeMapRows] = useState<ChargeMapRow[]>([]);

  // Charge mappings are keyed by retailer. The site's `current_retailer` is
  // often blank, which used to hide the "Map this description" control entirely
  // and read as "no mapping available" — fall back to the retailer the parser
  // read off the invoice itself.
  const retailerName = bill.site?.current_retailer || apr?.extracted_retailer || null;

  // Job 21: environmental charges are excluded from the comparison unless the
  // site has GreenPower. Hiding the section changes no total — subtotal, GST and
  // the overall variance come from the results, not from summing visible rows —
  // so the excluded amount is called out below when non-zero.
  const showEnv = showsEnvironmental(bill.site);

  const loadResolution = () => {
    void chargeMappingService.resolve(retailerName).then((r) => {
      if (r.success) setResolution(r.data ?? null);
    });
  };
  useEffect(loadResolution, [retailerName]);

  // The retailer charge map, keyed on the RESOLVED retailer id — not on
  // `retailerName`, which is what every other loader above takes.
  //
  // `bv_retailer_charge_map.retailer_id` is a foreign key, and the name on the
  // site is frequently an alias of it: the workbook says "AGL" while the
  // retailer row is "AGL Energy" carrying an "agl" alias. Fetching by the raw
  // name would therefore return nothing for exactly the retailers the map has
  // been seeded for, and the feature would look inert rather than broken.
  // `chargeMappingService.resolve` already does that alias lookup, so its
  // result is the only reliable key.
  // The whole approved-name group: the map may sit on a sibling row.
  const resolvedRetailerIds = (resolution?.group ?? []).map((r) => r.id).join(',');
  useEffect(() => {
    if (!resolvedRetailerIds) { setChargeMapRows([]); return; }
    let live = true;
    void chargeMapService.forRetailer(resolvedRetailerIds.split(',')).then((r) => {
      if (live) setChargeMapRows(r.success ? r.data ?? [] : []);
    });
    return () => { live = false; };
  }, [resolvedRetailerIds]);

  // Approved distributor names, so a map row for "Ergon" applies to a site
  // saying "Ergon Energy" (migration 138).
  const [distributorAliases, setDistributorAliases] = useState<NameAliases | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void chargeMapService.distributorAliases().then((a) => { if (live) setDistributorAliases(a); });
    return () => { live = false; };
  }, []);

  // `undefined`, never `{ rows: [], ... }`.
  //
  // `pairLines` branches on whether a map was supplied at all: given one, an
  // invoice line it cannot find is reported `unmapped` and pairs with nothing.
  // Handing it an empty rows array while resolution is still in flight — or for
  // a retailer whose sheet has not been seeded, which is still ~830 of ~855 rows
  // — would mark EVERY charge on the invoice unrecognised and empty the retailer
  // side of the variance. The absent object is what keeps today's alias pairing
  // in place for those bills.
  const mapContext = useMemo(
    () => buildMapContext(chargeMapRows, bill.site?.bv_extn, apr?.lines ?? [], distributorAliases),
    // Memoised because `pairLines` runs once per section: a fresh object each
    // render re-pairs and rebuilds every row on every render.
    [chargeMapRows, bill.site, apr, distributorAliases],
  );

  // The retailer name matches several rows, or none. Mapping cannot be saved until
  // a human says which retailer this site's charges belong to.
  const needsRetailerChoice = resolution?.status === 'ambiguous' || resolution?.status === 'none';
  // Candidate retailers to offer: the ambiguous set, or every retailer when 'none'.
  const retailerChoices: RetailerRow[] = !resolution
    ? []
    : resolution.status === 'ambiguous'
      ? resolution.candidates ?? []
      : resolution.retailers;

  const toggle = (key: ShadowBillSection) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Job 59: pair within each section, then regroup, so a charge renders and
  // totals under the section it belongs to rather than the one the calculation
  // or the retailer put it in. Shared with the report (comparisonSections.ts) so
  // the report's sections are exactly this table's.
  const pairedBySection = useMemo(
    () => comparisonRowsBySection(sr, apr, mapContext),
    [sr, apr, mapContext],
  );


  // Environmental is dropped from the comparison entirely when the site has no
  // GreenPower — before the Issues Only filter, so a hidden section never counts
  // as an issue.
  const comparableSections = SECTIONS.filter(({ key }) => key !== 'environmental' || showEnv);

  // Job 53: every total on this screen is derived from the rows, not read off
  // the stored result. An excluded charge leaves both sides, and a stored
  // section_total cannot know that.
  const totals = useMemo(() => {
    const rows = comparableSections.flatMap(({ key }) => pairedBySection[key] ?? []);
    return comparisonTotals(rows, effective, apr ? apr.gst : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairedBySection, effective, apr, showEnv]);

  const expectedOf = (key: ShadowBillSection) => totals.sections[key]?.expected ?? 0;
  const retailerOf = (key: ShadowBillSection) => (apr ? totals.sections[key]?.retailer ?? 0 : null);

  // What the charge map left out of each section's retailer figure. Derived ONCE
  // and read by both the Issues Only filter below and the note that explains the
  // gap on the section row: computed separately in the two places they could
  // disagree, and the filter would then admit a section whose note does not
  // render — or hide one whose note would have.
  const varianceGaps = useMemo(() => {
    const out = {} as Record<ShadowBillSection, { count: number; retailer: number }>;
    for (const { key } of SECTIONS) {
      out[key] = excludedFromVariance(pairedBySection[key] ?? [], effective);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairedBySection, effective]);

  // What TOTAL leaves out, so the table ties to the header's expected total and
  // invoice total (NMI 41037726865: "Matched" at $1,017.86 of a $1,973.73 invoice).
  const gstIsOff = effective.some((e) => e.section === 'gst' && e.description === null);
  const nc = notCompared({
    totals,
    unmappedRetailer: comparableSections.reduce((n, { key }) => n + (varianceGaps[key]?.retailer ?? 0), 0),
    unmappedCount: comparableSections.reduce((n, { key }) => n + (varianceGaps[key]?.count ?? 0), 0),
    environmental: showEnv ? null : {
      expected: sr.section_totals['environmental'] ?? 0,
      retailer: apr ? (apr.section_totals['environmental'] ?? 0) : 0,
    },
    gst: { off: gstIsOff, expected: sr.gst ?? 0, retailer: apr ? apr.gst ?? 0 : null },
    header: { expected: sr.total, invoice: apr ? apr.total : null },
  });
  const partialTotal = !!apr && nc.material;

  /**
   * Does this section need a validator's attention?
   *
   * The variance test is the original one and is unchanged. The second trigger
   * looks redundant — `comparisonTotals` deliberately dropped those rows, so the
   * arithmetic reports no variance at all — and that is exactly why it is here:
   *
   *  - `unmapped` means we do not recognise the charge, so we cannot say whether
   *    it is correct. An open question is not a pass.
   *  - `passthrough` means no validation was possible. An explicit "not checked"
   *    is not a pass either.
   *
   * Without it, a section holding nothing BUT unrecognised charges sums to a
   * zero variance, reads as matched, and vanishes under Issues Only — taking the
   * excluded-from-variance note with it. The filter's emptiness would then read
   * as an assurance that nothing needs looking at, which is the opposite of what
   * an unrecognised charge on a customer's bill means. Do not remove it because
   * the totals say there is no variance; that is the point.
   *
   * The three-way `classify` result is left alone on purpose: `over`/`under`
   * assert a dollar direction, and an unmapped row has no expected side to give
   * one. The section's Difference and Variance cells keep reporting the
   * arithmetic honestly; the amber note beneath the row is what says "needs a
   * look".
   */
  const sectionHasIssue = (key: ShadowBillSection): boolean => {
    const k = classify(expectedOf(key), retailerOf(key));
    if (k === 'over' || k === 'under') return true;
    // An unmapped charge is an open question even when it is too small to move
    // the section out of "matched".
    if ((pairedBySection[key] ?? []).some((r) => r.unmapped && !isRowExcluded(r, effective))) return true;
    return (varianceGaps[key]?.count ?? 0) > 0;
  };

  const issueCount = comparableSections.filter(({ key }) => sectionHasIssue(key)).length;

  const visibleSections = comparableSections.filter(({ key }) =>
    filter === 'all' || sectionHasIssue(key));

  const totalKind = classify(totals.expectedTotal, totals.retailerTotal);

  // Job 53: switch one charge in or out. A charge covered by a whole-section
  // exclusion is included again by turning that section exclusion into per-charge
  // exclusions of its siblings — otherwise the only way back would be to restore
  // the entire section.
  const toggleCharge = (row: ChargeRow) => {
    if (!onExclusionsChange) return;
    const sectionWide = exclusions.some((e) => e.section === row.section && e.description === null);
    if (sectionWide) {
      const siblings = (pairedBySection[row.section] ?? [])
        .filter((r) => norm(r.label) !== norm(row.label))
        .map((r) => ({ section: r.section, description: r.label }));
      onExclusionsChange([
        ...exclusions.filter((e) => !(e.section === row.section && e.description === null)),
        ...siblings,
      ]);
      return;
    }
    const hit = (e: CalcExclusion) =>
      e.section === row.section && e.description !== null && norm(e.description) === norm(row.label);
    onExclusionsChange(
      exclusions.some(hit)
        ? exclusions.filter((e) => !hit(e))
        : [...exclusions, { section: row.section, description: row.label }],
    );
  };

  // Switching a section off replaces any per-charge exclusions in it with one
  // whole-section exclusion, so the stored list stays the smallest thing that
  // describes the state.
  // GST has no charge lines of its own, so it is switched as a whole section and
  // nothing else. Off, the comparison runs ex-GST on both sides.
  const gstOff = exclusions.some((e) => e.section === 'gst' && e.description === null);
  const toggleGst = () => {
    if (!onExclusionsChange) return;
    onExclusionsChange(
      gstOff
        ? exclusions.filter((e) => e.section !== 'gst')
        : [...exclusions, { section: 'gst', description: null }],
    );
  };

  const toggleSection = (section: ShadowBillSection) => {
    if (!onExclusionsChange) return;
    const rows = pairedBySection[section] ?? [];
    const allOff = rows.length > 0 && rows.every((r) => isRowExcluded(r, exclusions));
    const rest = exclusions.filter((e) => e.section !== section);
    onExclusionsChange(allOff ? rest : [...rest, { section, description: null }]);
  };

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5">
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-all',
              filter === 'all' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800',
            )}
          >
            All Sections
          </button>
          <button
            onClick={() => setFilter('issues')}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1',
              filter === 'issues' ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-800',
            )}
          >
            Issues Only
            {issueCount > 0 && (
              <span className={cn(
                'inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold',
                filter === 'issues' ? 'bg-white text-red-600' : 'bg-red-100 text-red-600',
              )}>
                {issueCount}
              </span>
            )}
          </button>
        </div>

        {!apr && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            Parse invoice to enable comparison
          </span>
        )}
        {apr && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            Invoice parsed · {apr.confidence} confidence
          </span>
        )}
        {apr?.match_warning && (
          <span className="text-xs font-medium text-red-700 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> {apr.match_warning}
          </span>
        )}
        {/* These figures may be hand-corrected rather than what the parser read.
            The comparison drives what a client is told; whether a person moved a
            number belongs beside it, not two screens away. */}
        {editedSummary(bill) && (
          <button
            type="button"
            onClick={() => setComparingEdits(true)}
            className="text-xs font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-0.5 hover:bg-amber-200 transition-colors"
            title="See exactly which values were changed"
          >
            ✎ {editedSummary(bill)} — compare
          </button>
        )}
      </div>

      {/* Job 53: what the exclusions removed, stated on both sides. A total that
          leaves charges out has to say so, or it reads as a clean bill. */}
      {totals.excluded.count > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
          <span className="font-medium text-gray-700">
            {totals.excluded.count} {totals.excluded.count === 1 ? 'charge' : 'charges'} excluded
          </span>
          <span>
            Expected −{fmt(totals.excluded.expected)}
            {apr ? ` · Retailer −${fmt(totals.excluded.retailer)}` : ''} — the totals below already leave these out.
          </span>
          {canToggle && (
            <button
              type="button"
              className="text-blue-600 hover:underline ml-auto"
              onClick={() => onExclusionsChange?.([])}
            >
              Include all again
            </button>
          )}
        </div>
      )}

      <InvoiceCompareDialog
        open={comparingEdits}
        bill={bill}
        onClose={() => setComparingEdits(false)}
      />

      {/* Retailer unresolved — mapping is disabled until a retailer is chosen */}
      {apr && needsRetailerChoice && (
        <div className="flex items-start gap-1.5 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Charge mapping is unavailable: the retailer name{' '}
            <span className="font-medium">"{retailerName}"</span>{' '}
            {resolution?.status === 'none'
              ? 'does not match any retailer on record'
              : `matches ${retailerChoices.length} retailers (${retailerChoices.map((r) => r.name).join(', ')})`}
            . When you map an invoice charge below, choose which retailer this site belongs to —
            it is remembered so you are not asked again.
          </span>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-900 text-white text-xs uppercase tracking-wide">
            <th className="px-4 py-3 text-left font-semibold w-[36%]">Charge</th>
            <th className="px-4 py-3 text-right font-semibold w-[16%] text-gray-300">
              Expected <span className="text-gray-500 normal-case tracking-normal font-normal">(contracted)</span>
            </th>
            <th className="px-4 py-3 text-right font-semibold w-[16%] text-gray-300">
              Retailer <span className="text-gray-500 normal-case tracking-normal font-normal">(billed)</span>
            </th>
            <th className="px-4 py-3 text-right font-semibold w-[16%]">Difference</th>
            <th className="px-4 py-3 text-center font-semibold w-[16%]">Variance</th>
          </tr>
        </thead>

        <tbody>
          {visibleSections.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">
                No discrepancies — all sections matched.
              </td>
            </tr>
          )}

          {visibleSections.map(({ key, label }) => {
            const shadowTotal = expectedOf(key);
            const actualTotal = retailerOf(key);
            const kind = classify(shadowTotal, actualTotal);
            const sectionRows = pairedBySection[key] ?? [];
            const sectionOff = sectionRows.length > 0 && sectionRows.every((r) => isRowExcluded(r, exclusions));
            const diff = actualTotal != null ? actualTotal - shadowTotal : null;
            const isExpanded = expanded.has(key);
            const paired = pairedBySection[key] ?? [];
            // What the charge map left out of THIS section's retailer figure —
            // the same value `sectionHasIssue` admitted this section on, so the
            // note is guaranteed to render wherever the filter let the section
            // through. Derived from the rows already paired above, never
            // recomputed from the source lines.
            const varianceGap = varianceGaps[key] ?? { count: 0, retailer: 0 };

            return (
              <Fragment key={key}>
                {/* Section row */}
                <tr
                  onClick={() => toggle(key)}
                  className={cn(
                    'border-t border-gray-100 cursor-pointer select-none group',
                    isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50/70',
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
                        {isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5" />
                          : <ChevronRight className="h-3.5 w-3.5" />}
                      </span>
                      <span className={cn(
                        'font-semibold text-sm',
                        sectionOff ? 'text-gray-400 line-through' : 'text-gray-800',
                      )}>
                        {label}
                      </span>
                      {kind === 'over' && !sectionOff && (
                        <span className="text-[10px] font-medium text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                          Overcharged
                        </span>
                      )}
                      {kind === 'under' && !sectionOff && (
                        <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                          Undercharged
                        </span>
                      )}
                      {/* Job 53: switch the whole section out of the calculation. */}
                      {canToggle && sectionRows.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1.5 ml-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            checked={!sectionOff}
                            onCheckedChange={() => toggleSection(key)}
                            className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
                            aria-label={`${sectionOff ? 'Include' : 'Exclude'} the ${label} section`}
                            title={sectionOff
                              ? 'Off — this whole section is left out of both totals.'
                              : 'On — this section is counted. Switch off to leave every charge in it out.'}
                          />
                          <span className={cn(
                            'text-[10px] font-medium',
                            sectionOff ? 'text-gray-500' : 'text-gray-400',
                          )}>
                            {sectionOff ? 'Section excluded' : 'Section counted'}
                          </span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-700">
                    {fmt(shadowTotal)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-700">
                    {actualTotal != null ? fmt(actualTotal) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {diff != null ? (
                      <span className={cn(
                        kind === 'matched' ? 'text-emerald-600' :
                        kind === 'over'    ? 'text-red-600'     : 'text-amber-600',
                      )}>
                        {diff >= 0 ? '+' : ''}{fmt(diff)}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <VarianceCell shadow={shadowTotal} actual={actualTotal} />
                </tr>

                {/* The section's retailer figure above no longer ties to the printed
                    invoice once the map leaves a line out of the variance, and the
                    difference is invisible without this. Rendered on the OUTER table
                    beside the totals it explains — not inside the expanded breakdown,
                    which a reader can collapse, taking the explanation with it. */}
                {varianceGap.count > 0 && (
                  <tr className="bg-white">
                    <td colSpan={5} className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        <span>
                          <span className="font-medium text-gray-700">
                            {varianceGap.count} pass-through {varianceGap.count === 1 ? 'charge' : 'charges'} added
                            back — {fmt(varianceGap.retailer)}
                          </span>
                          {' '}set to Passthrough in the retailer charge map, so the retailer figure above
                          leaves it out and it is taken as billed.
                          {isExpanded
                            ? ' Marked Pass-through in the breakdown below.'
                            : ' Expand the section to see which.'}
                        </span>
                      </span>
                    </td>
                  </tr>
                )}

                {/* Expanded detail */}
                {isExpanded && (
                  <tr>
                    <td colSpan={5} className="px-6 pb-3 pt-0 bg-gray-50 border-t border-gray-100">
                      <p className="text-[10px] text-gray-400 mb-1.5">
                        Each charge shows two rows — <span className="text-gray-600 font-medium">Expected Invoice</span>{' '}
                        (what the contract says) and <span className="text-gray-600 font-medium">Retailer Invoice</span>{' '}
                        (what was billed). <span className="text-gray-600 font-medium">QTY</span> is how many of the
                        thing the rate is charged per — meters, service points, kW/kVA of demand.{' '}
                        <span className="text-gray-600 font-medium">Usage</span> is what the charge ran over — kWh
                        consumed, or days. Metering and demand fill both: 1 meter across 31 days. Usage is the
                        metered figure as billed; the loss factor applied to it is its own column, and applies to
                        the expected side only — retailers bill on metered quantities.
                      </p>
                      <table className="w-full text-xs rounded-lg overflow-hidden border border-gray-200">
                        <thead>
                          <tr className="bg-gray-100 border-b border-gray-200 text-gray-500 uppercase tracking-wide">
                            <th className="px-3 py-2 text-left font-medium w-[26%]">Charge Type</th>
                            <th className="px-3 py-2 text-right font-medium w-[18%]">Rate</th>
                            <th className="px-3 py-2 text-right font-medium w-[15%]">QTY</th>
                            <th className="px-3 py-2 text-right font-medium w-[18%]">Usage</th>
                            <th className="px-3 py-2 text-right font-medium w-[11%]">Loss Factor</th>
                            <th className="px-3 py-2 text-right font-medium w-[12%]">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {paired.map((p) => {
                            const expected = band(p.shadow);
                            const retailer = band(p.actual);
                            return (
                              <Fragment key={p.key}>
                                {/* Charge header — carries the name, provenance and mapping control */}
                                <tr className="bg-gray-50/70">
                                  <td colSpan={6} className="px-3 pt-2 pb-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={cn(
                                        'font-medium',
                                        isRowExcluded(p, exclusions) ? 'text-gray-400 line-through' : 'text-gray-700',
                                      )}>
                                        {p.label}
                                      </span>
                                      {/* Job 53: an on/off switch, not a text link. As grey text beside
                                          the charge name this read as a label and went unfound — the
                                          same way it went unfound on the Expected Invoice tab. */}
                                      {canToggle && (
                                      <span className="inline-flex items-center gap-1.5">
                                        <Switch
                                          checked={!isRowExcluded(p, exclusions)}
                                          onCheckedChange={() => toggleCharge(p)}
                                          className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
                                          aria-label={`${isRowExcluded(p, exclusions) ? 'Include' : 'Exclude'} ${p.label}`}
                                          title={isRowExcluded(p, exclusions)
                                            ? 'Off — left out of both totals. Switch on to count it again.'
                                            : 'On — counted in both totals. Switch off to leave it out.'}
                                        />
                                        <span className={cn(
                                          'text-[10px] font-medium',
                                          isRowExcluded(p, exclusions) ? 'text-gray-500' : 'text-emerald-600',
                                        )}>
                                          {isRowExcluded(p, exclusions) ? 'Excluded from both sides' : 'Counted'}
                                        </span>
                                      </span>
                                      )}
                                      {!canToggle && isRowExcluded(p, exclusions) && (
                                        <span className="text-[10px] font-medium text-gray-500">
                                          Excluded from both sides
                                        </span>
                                      )}
                                      {/* Job 59: put the charge in the section it belongs to. Saved
                                          against the retailer, so their next invoice lands right. */}
                                      {p.movedFrom && (
                                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                                          moved from {SECTIONS.find((s) => s.key === p.movedFrom)?.label ?? p.movedFrom}
                                        </span>
                                      )}
                                      {/* The retailer charge map found nothing for this invoice wording.
                                          Counted in the retailer total since 2026-10-07, so it shows as
                                          variance until it is mapped. */}
                                      {p.unmapped && (
                                        <span
                                          className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded"
                                          title="Not in the retailer charge map — counted in the retailer total, with nothing expected against it. Map it to compare it."
                                        >
                                          Unmapped
                                        </span>
                                      )}
                                      {/* Charge map: Passthrough (VALIDATION POSSIBLE = N). Left out of
                                          the variance and added back as billed. */}
                                      {p.passthrough && (
                                        <span
                                          className="text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded"
                                          title="Set to Passthrough in the retailer charge map: taken as billed and left out of the variance."
                                        >
                                          Pass-through
                                        </span>
                                      )}
                                      {/* Read-only now. The per-retailer "Move to…" override that used
                                          to sit here is gone: section placement comes from the charge
                                          map's `expected_section`, applied in `pairLines`, with the
                                          canonical rule still winning over both. A charge filed
                                          somewhere unexpected is corrected in bv_retailer_charge_map,
                                          not bill by bill. */}
                                      {canonicalSectionFor(p.label, p.section) && (
                                        <span
                                          className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded"
                                          title={`${p.label} is always shown under ${
                                            SECTIONS.find((s) => s.key === canonicalSectionFor(p.label, p.section))?.label
                                            ?? canonicalSectionFor(p.label, p.section)
                                          }.`}
                                        >
                                          Always in {SECTIONS.find((s) => s.key === canonicalSectionFor(p.label, p.section))?.label
                                            ?? canonicalSectionFor(p.label, p.section)}
                                        </span>
                                      )}
                                      {expected && retailer && (
                                        <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">Both</span>
                                      )}
                                      {expected && !retailer && (
                                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-medium">Expected only</span>
                                      )}
                                      {!expected && retailer && (
                                        <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">Invoice only</span>
                                      )}
                                      {p.raw && (
                                        <span className="text-[10px] text-gray-400 italic">invoice: "{p.raw}"</span>
                                      )}
                                      {/* A row keyed by canonical name can stand in for several invoice
                                          lines that the charge map folds together. Say so, or one figure
                                          standing for four charges reads as the table losing lines. */}
                                      {retailerName && invoiceLineCount(p) > 1 && (
                                        <span
                                          className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded"
                                          title={`This row adds up ${invoiceLineCount(p)} invoice lines, folded together by the retailer charge map.`}
                                        >
                                          {invoiceLineCount(p)} invoice lines
                                        </span>
                                      )}
                                      {/* Where a mapped row's retailer figures came from — the
                                          invoice's OWN wording, which the charge map renamed to this
                                          row's vocabulary label. Without it a row reading "Anytime
                                          Demand" gives no clue it was billed as "Capacity Charge",
                                          and a collapsed group shows one figure with the four charges
                                          behind it invisible.

                                          Comparison only, never the report: the client is shown the
                                          charge, not our mapping of it. `BillReportScreen` and
                                          `bvReportContextService` read section totals and the
                                          invoice's own lines, and neither touches `mappedRaws`.

                                          Read-only: a map hit comes from `bv_retailer_charge_map`
                                          and is corrected there. The Unmap control that used to sit
                                          here deleted a retailer.extn charge alias, and that whole
                                          path went with the Map/Re-map picker. */}
                                      {mapContext && (p.mappedRaws?.length ?? 0) > 0 && (
                                        <span
                                          className="text-[10px] text-gray-400 italic"
                                          title="The retailer's own wording for this charge, resolved through the retailer charge map. Not shown on the report."
                                        >
                                          mapped from {(p.mappedRaws ?? []).map((raw) => `"${raw}"`).join(', ')}
                                        </span>
                                      )}
                                      {/* Not in the charge map, paired because the invoice uses the
                                          identical name. Said so, so a reviewer can add a map row if
                                          the pairing is ever wrong. */}
                                      {p.nameMatched && (
                                        <span
                                          data-testid="comparison-name-matched"
                                          className="text-[10px] text-gray-400 italic"
                                          title="This invoice line is not in the retailer charge map. It was paired because its name is identical to the expected charge."
                                        >
                                          matched by name
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                <BandRow
                                  label="Expected Invoice"
                                  self={expected}
                                  other={retailer}
                                  onEditQty={onEditShadowQty}
                                  onEditUsage={onEditShadowUsage}
                                  onOpenEditor={onOpenExpectedEditor}
                                />
                                <BandRow
                                  label="Retailer Invoice"
                                  self={retailer}
                                  other={expected}
                                  muted
                                  onEditQty={onEditActualQty}
                                  onEditUsage={onEditActualUsage}
                                  onOpenEditor={onOpenRetailerEditor}
                                />
                              </Fragment>
                            );
                          })}
                          {paired.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-2 text-center text-gray-400 italic">
                                No charge lines in this section
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}

          {/* Environmental exclusion note — keeps the section-vs-total gap explainable */}
          {!showEnv && (() => {
            const envShadow = sr.section_totals['environmental'] ?? 0;
            const envActual = apr ? (apr.section_totals['environmental'] ?? 0) : 0;
            if (envShadow === 0 && envActual === 0) return null;
            return (
              <tr className="border-t border-gray-100 bg-white">
                <td colSpan={5} className="px-4 py-2.5 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-gray-400" />
                    Environmental charges are excluded from this comparison (site has no GreenPower):
                    expected {fmt(envShadow)}{apr ? `, retailer ${fmt(envActual)}` : ''}.
                  </span>
                </td>
              </tr>
            );
          })()}
        </tbody>

        {/* ── Summary footer ───────────────────────────────────────────── */}
        <tfoot className="border-t-2 border-gray-200">
          {/* Subtotal */}
          <tr className="bg-gray-50">
            <td className="px-4 py-3 text-sm font-medium text-gray-600 pl-10">
              Subtotal <span className="text-gray-400 font-normal text-xs">(excl. GST)</span>
            </td>
            <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-700">{fmt(totals.expectedSubtotal)}</td>
            <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-700">
              {apr ? fmt(totals.retailerSubtotal) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-4 py-3 text-right tabular-nums font-medium">
              {apr ? (
                <span className={cn(
                  classify(totals.expectedSubtotal, totals.retailerSubtotal) === 'matched' ? 'text-emerald-600' :
                  classify(totals.expectedSubtotal, totals.retailerSubtotal) === 'over'    ? 'text-red-600'     : 'text-amber-600',
                )}>
                  {totals.retailerSubtotal - totals.expectedSubtotal >= 0 ? '+' : ''}{fmt(totals.retailerSubtotal - totals.expectedSubtotal)}
                </span>
              ) : <span className="text-gray-300">—</span>}
            </td>
            <VarianceCell shadow={totals.expectedSubtotal} actual={apr ? totals.retailerSubtotal : null} />
          </tr>

          {/* GST */}
          <tr className={cn('border-t border-gray-200', gstOff ? 'bg-gray-100' : 'bg-gray-50')}>
            <td className="px-4 py-2.5 text-sm text-gray-600 pl-4">
              <div className="flex items-center gap-2">
                {canToggle && (
                  <Switch
                    checked={!gstOff}
                    onCheckedChange={toggleGst}
                    className="scale-75 data-[state=checked]:bg-emerald-500"
                    aria-label={gstOff ? 'Include GST' : 'Exclude GST'}
                  />
                )}
                <span className={cn(gstOff && 'text-gray-400 line-through')}>
                  GST <span className="text-gray-400 text-xs">(10%)</span>
                </span>
              </div>
            </td>
            <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmt(totals.expectedGst)}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
              {totals.retailerGst != null ? fmt(totals.retailerGst) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-4 py-2.5 text-right tabular-nums">
              {apr ? (
                <span className={cn(
                  classify(totals.expectedGst, totals.retailerGst) === 'matched' ? 'text-emerald-600' :
                  classify(totals.expectedGst, totals.retailerGst) === 'over'    ? 'text-red-600'     : 'text-amber-600',
                )}>
                  {(totals.retailerGst ?? 0) - totals.expectedGst >= 0 ? '+' : ''}{fmt((totals.retailerGst ?? 0) - totals.expectedGst)}
                </span>
              ) : <span className="text-gray-300">—</span>}
            </td>
            <VarianceCell shadow={totals.expectedGst} actual={totals.retailerGst} />
          </tr>

          {/* Total */}
          <tr className={cn(
            'border-t-2',
            totalKind === 'over'    ? 'bg-red-50 border-red-200'     :
            totalKind === 'under'   ? 'bg-amber-50 border-amber-200' :
            totalKind === 'matched' ? 'bg-emerald-50 border-emerald-200' :
            'bg-gray-100 border-gray-300',
          )}>
            <td className="px-4 py-4 text-sm font-bold text-gray-900 pl-10 uppercase tracking-wide">
              Total
              {partialTotal && (
                <span data-testid="comparison-total-partial" className="block text-[10px] font-medium normal-case tracking-normal text-gray-500">
                  compared charges only — see below
                </span>
              )}
            </td>
            <td className="px-4 py-4 text-right tabular-nums font-bold text-gray-900">{fmt(totals.expectedTotal)}</td>
            <td className="px-4 py-4 text-right tabular-nums font-bold text-gray-900">
              {totals.retailerTotal != null ? fmt(totals.retailerTotal) : <span className="text-gray-300 font-normal">—</span>}
            </td>
            <td className="px-4 py-4 text-right tabular-nums font-bold">
              {apr ? (
                <span className={cn(
                  totalKind === 'matched' ? 'text-emerald-700' :
                  totalKind === 'over'    ? 'text-red-700'     : 'text-amber-700',
                )}>
                  {(totals.retailerTotal ?? 0) - totals.expectedTotal >= 0 ? '+' : ''}{fmt((totals.retailerTotal ?? 0) - totals.expectedTotal)}
                </span>
              ) : <span className="text-gray-300">—</span>}
            </td>
            <VarianceCell shadow={totals.expectedTotal} actual={totals.retailerTotal} bold />
          </tr>

          {/* ── Not compared: ties the table back to the header totals ──── */}
          {partialTotal && (
            <>
              <tr data-testid="comparison-not-compared" className="border-t border-gray-200 bg-white">
                <td colSpan={5} className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Not compared — left out of the total above
                </td>
              </tr>
              {nc.lines.map((l) => (
                <tr key={l.key} data-testid={`comparison-not-compared-${l.key}`} className="bg-white">
                  <td className="px-4 py-1.5 text-sm text-gray-600 pl-10">{l.label}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-gray-600">{fmt(l.expected)}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-gray-600">{fmt(l.retailer)}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-gray-500">
                    {l.retailer - l.expected >= 0 ? '+' : ''}{fmt(l.retailer - l.expected)}
                  </td>
                  <td />
                </tr>
              ))}
              <tr data-testid="comparison-whole-invoice" className="border-t-2 border-gray-300 bg-gray-50">
                <td className="px-4 py-3 text-sm font-bold text-gray-900 pl-10">
                  Whole invoice
                  {!nc.reconciles && (
                    <span className="block text-[10px] font-medium text-amber-700">
                      Does not add up to the header totals exactly — check the parsed invoice lines
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900">{fmt(nc.wholeExpected)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900">{fmt(nc.wholeRetailer)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-700">
                  {nc.wholeRetailer - nc.wholeExpected >= 0 ? '+' : ''}{fmt(nc.wholeRetailer - nc.wholeExpected)}
                </td>
                <VarianceCell shadow={nc.wholeExpected} actual={nc.wholeRetailer} bold />
              </tr>
            </>
          )}
        </tfoot>
      </table>
    </div>
  );
}
